import { base64ToBlob } from '@/utils/base64ToBlob';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { BrandsEndpoints } from './brandsEndpoints';
import type { IBrandsService } from './IBrandsService';
import type { BrandListItem } from '@/api/models';
import {
    ALL_BRANDS_QUERY,
    type BrandCreateRequest,
    type BrandListQuery,
    type BrandWriteRequest,
} from './requests';
import type { BrandWriteResponse, GetBrandListResponse } from './responses';

type BrandsServiceOptions = {
    baseUrl: string;
};

export class BrandsService implements IBrandsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: BrandsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public getBrandList(
        query: BrandListQuery,
    ): Promise<ApiResponse<GetBrandListResponse>> {
        const url = BrandsEndpoints.list();
        // The bos-api binds these case-insensitively; `search` is omitted when
        // blank so an empty box returns the unfiltered page.
        const params: Record<string, string | number> = {
            page: query.page,
            pageSize: query.pageSize,
        };
        const search = query.search?.trim();
        if (search) {
            params.search = search;
        }
        return makeApiRequest<GetBrandListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, { params }),
        );
    }

    /**
     * Every brand (BOS-201). The API caps a page at
     * `BRAND_LIST_MAX_PAGE_SIZE`, so this reads the first page, then
     * the rest together once it knows how many there are. Any page failing
     * fails the whole read: a brand filter missing half its brands would look
     * complete.
     */
    public async getAllBrands(): Promise<ApiResponse<BrandListItem[]>> {
        const first = await this.getBrandList(ALL_BRANDS_QUERY);
        if (!first.success) {
            return first;
        }
        const pages = Array.from(
            { length: Math.max(first.response.totalPages - 1, 0) },
            (_, index) =>
                this.getBrandList({ ...ALL_BRANDS_QUERY, page: index + 2 }),
        );
        const brands = [...first.response.items];
        for (const page of await Promise.all(pages)) {
            if (!page.success) {
                return page;
            }
            brands.push(...page.response.items);
        }
        return { success: true, response: brands };
    }

    public createBrand(
        body: BrandCreateRequest,
    ): Promise<ApiResponse<BrandWriteResponse>> {
        const url = BrandsEndpoints.create();

        // bos-api binds [FromForm] on POST /brands — name (required) and an
        // optional `file`. Packing as multipart lets the server validate the
        // image and persist the brand in one round-trip.
        const formData = new FormData();
        formData.append('name', body.name);
        if (body.imageUpload) {
            formData.append(
                'file',
                base64ToBlob(
                    body.imageUpload.dataBase64,
                    body.imageUpload.mimeType,
                ),
                body.imageUpload.fileName,
            );
        }

        return makeApiRequest<BrandWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, formData),
        );
    }

    public updateBrand(
        id: string,
        body: BrandWriteRequest,
    ): Promise<ApiResponse<BrandWriteResponse>> {
        const url = BrandsEndpoints.update(id);
        return makeApiRequest<BrandWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }

    public deleteBrand(id: string): Promise<ApiResponse<void>> {
        const url = BrandsEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }
}
